import { CircularProgress, Grid, Icon, Typography, Button } from '@mui/material';
import { MonetizationOn, CheckCircle as CheckCircleIcon, RemoveCircleOutline } from '@mui/icons-material';
import React, { useEffect } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { ButtonAdd, ButtonCancel, ButtonContinue, IconGenerator } from '../../../components/general/mini_components';
import PatientInfo from '../../../components/PatientInfo';
import { IAppointment, IAgendasConfigParams, AgendasTypes, DEFAULT_CLINIC_TIMEZONE } from '../../../constants/types';
import { getPatientsAppoinmentsService, cancelAppointmentService, updateAppoinmentsService } from '../../../services/agenda';
import { dateAndTimeFromPostgresString, fullDateFromPostgresString, researcherFullName, stringDatePostgresToDate, timeFromPostgresString, turnsToSchedule } from '../../../utils/index.jsx';
import { RequestStatus } from '../Request/types';
import { FormMakeAppointment } from './FormAppointment';
import Modal from '../../../components/general/modal';
import { useVoucherBillingConfirm } from '../../../hooks/useVoucherBillingConfirm';
import { canCancelAppointment, canShowUpAppointment, isAppointmentDone } from '../../../utils/agenda';
import EventIcon from '@mui/icons-material/Event';
import { generateBillingUrl } from '../../../utils/urlHelpers';
import { useHistory } from 'react-router-dom';
import { HOSPITAL_OUTPATIENTS_PATIENT_ROUTE, HOSPITAL_OUTPATIENTS_APPOINTMENT } from '../../../routes/urls';
import { getLastValidConsultationAppointment } from '../../../utils/appointmentPrefill';
import { getAppointmentDepartmentName, mergeAppointmentAfterCheckIn } from '../../../utils/patientAppointmentTable';
import QuickPayButton from '../../../components/QuickPayButton';
import { useSelector } from 'react-redux';
import { ReduxStore } from '../../../constants/types';

interface PatientAppointmentInfoProps {
    uuidPatient: string;
    uuidInvestigation: string;
    outpatientsInfo: IAgendasConfigParams;
    phoneNumber: string;
    resetModal: () => void;
    appointmentMadeCallback: () => void;
}

const PatientAppointmentInfo: React.FC<PatientAppointmentInfoProps> = ({ uuidPatient, uuidInvestigation, outpatientsInfo, phoneNumber,
                                                                            appointmentMadeCallback, resetModal }) => {
    const [patientsAppointments, setPatientsAppointments] = React.useState<IAppointment[]>([]);
    const [loadingPatientsAppointments, setLoadingPatientsAppointments] = React.useState<boolean>(true);
    const [lastUpdate, setLastUpdate] = React.useState(new Date());
    // SHER-395: shared "no forfait session → bill the consultation?" confirm dialog + retry.
    const { handleCheckInError, voucherConfirmModal } = useVoucherBillingConfirm();
    const currency = useSelector((state: ReduxStore) => state.investigations.currentInvestigation?.billingInfo?.currency ?? 'FCFA');

    useEffect(() => {
        setLoadingPatientsAppointments(true);
        getPatientsAppoinmentsService(uuidInvestigation, uuidPatient, AgendasTypes.CONSULTATION)
            .then(response => {
                setPatientsAppointments(response.appointments)
                setLoadingPatientsAppointments(false);

            })
            .catch(err => {
                let message;
                if(err.response.status === 401){
                    message = "No tiene permisos para realizar esta acción";
                }
                
                setLoadingPatientsAppointments(false);
            })
    }, []);

    function showUpPatient(idAppointment:number, forceBilling:boolean = false){
        setLoadingPatientsAppointments(true);
        const appointmentToUpdate = patientsAppointments.find(appointment => appointment.id === idAppointment);
        if(appointmentToUpdate){
            const uuidAppointment = appointmentToUpdate.uuid;
            updateAppoinmentsService(uuidInvestigation, uuidAppointment, forceBilling)
                .then(response => {
                    const indexAppointment = patientsAppointments.findIndex((appointment) => appointment.uuid === uuidAppointment);
                    if(indexAppointment !== -1){
                        const updated = [...patientsAppointments];
                        updated[indexAppointment] = mergeAppointmentAfterCheckIn(
                            patientsAppointments[indexAppointment],
                            response.appointment,
                        );
                        setPatientsAppointments(updated);
                    }
                    //setShowSnackbar({show:true, message:"pages.hospital.outpatients.checkin_success", severity:"success"});
                    setLoadingPatientsAppointments(false);
                    setLastUpdate(new Date());
                })
                .catch(err => {
                    // SHER-395: no available forfait session → confirm dialog, then retry with
                    // forceBilling (shared hook). Only on the first attempt to avoid a loop.
                    if(!forceBilling && handleCheckInError(err, () => showUpPatient(idAppointment, true))){
                        setLoadingPatientsAppointments(false);
                        return;
                    }
                    let message = "general.error";
                    if(err.response?.status === 401){
                        message = "pages.hospital.outpatients.no_permissions";
                    }
                    //setShowSnackbar({show:true, message:message, severity:"error"});
                    setLoadingPatientsAppointments(false);
                })
        }

    }

    function cancelAppointment(idAppointment:number, byUser:boolean = false){
        setLoadingPatientsAppointments(true);
        const appointmentToDelete = patientsAppointments.find(appointment => appointment.id === idAppointment);
        if(appointmentToDelete){
            cancelAppointmentService(uuidInvestigation, appointmentToDelete.uuid, byUser)
            .then(response => {
                const indexAppointment = patientsAppointments.findIndex((appointment) => appointment.uuid === appointmentToDelete.uuid);
                if(indexAppointment !== -1){
                    patientsAppointments[indexAppointment].requestAppointment.status = byUser ? RequestStatus.CANCELED_BY_USER : RequestStatus.CANCELED;
                }
                setLoadingPatientsAppointments(false);
                setLastUpdate(new Date());
            })
            .catch(err => {
                setLoadingPatientsAppointments(false);
            })
        }
    }

    return (
        <>
            <PatientAppointmentInfoLocalized key={lastUpdate.getTime()} uuidPatient={uuidPatient} uuidInvestigation={uuidInvestigation} resetModal={resetModal}
                appointmentMadeCallback={appointmentMadeCallback} patientsAppointments={patientsAppointments}
                loadingPatientsAppointments={loadingPatientsAppointments}
                outpatientsInfo={outpatientsInfo} phoneNumber={phoneNumber} currency={currency}
                cancelAppointmentCallback={(idAppointment:number, byUser:boolean) => cancelAppointment(idAppointment, byUser)}
                showUpPatientCallback={(idAppointment:number) => showUpPatient(idAppointment)}
                />
            {voucherConfirmModal}
        </>
    );
};

export default PatientAppointmentInfo;

interface PatientAppointmentInfoCoreProps extends PatientAppointmentInfoProps, LocalizeContextProps {
    patientsAppointments:IAppointment[];
    outpatientsInfo: IAgendasConfigParams;
    loadingPatientsAppointments: boolean;
    currency: string;
    cancelAppointmentCallback: (id:number, byUser:boolean) => void;
    showUpPatientCallback: (id:number) => void;
}

const PatientAppointmentInfoCore: React.FC<PatientAppointmentInfoCoreProps> = ({ uuidPatient, uuidInvestigation, activeLanguage, loadingPatientsAppointments,
                                                                                    outpatientsInfo, phoneNumber, currency,
                                                                                    patientsAppointments, appointmentMadeCallback, showUpPatientCallback, cancelAppointmentCallback }) => {
    const [createAppointment, setCreateAppointment] = React.useState<boolean>(false);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [showupAppointmentId, setShowupAppointmentId] = React.useState<number>(0);
    const [cancelAppointmentId, setCancelAppointmentId] = React.useState<number>(0);
    const history = useHistory();
    const prefillTemplate = React.useMemo(
        () => getLastValidConsultationAppointment(patientsAppointments),
        [patientsAppointments],
    );

    function handleCreateAppointment() {
        if (outpatientsInfo.consultation.type === "date_time") {
            const nextUrl = HOSPITAL_OUTPATIENTS_PATIENT_ROUTE.replace(":uuidPatient", uuidPatient);
            history.push(nextUrl);
        } else {
            setCreateAppointment(true);
        }
    }
   
    function renderShowIcon(status:number, date:number){
        if(isAppointmentDone(status)){
            return (
                <Typography variant='body2'  component="div" style={{textAlign:"center"}} >
                    <CheckCircleIcon style={{fontSize:"1.2rem", color:"green"}} /> 
                </Typography>
            )
        }
        else if(status === RequestStatus.PENDING_PAYMENT){
            return(
                <Typography variant='body2'  component="div" style={{textAlign:"center"}} >
                    <MonetizationOn style={{fontSize:"1.2rem", color:"red"}} />
                </Typography>
            )
        }
        else if(status === RequestStatus.PENDING_APPROVAL && stringDatePostgresToDate(date) > new Date()){//Is an appointment that is pending to be approved, meaning the user didnt show up
            return (
                <Typography variant='body2'  component="div" style={{textAlign:"center"}} >
                <EventIcon style={{fontSize:"1.5rem", color:"DodgerBlue", textAlign:"center"}} /> 
                </Typography>
            )
        }
        else if(status === RequestStatus.CANCELED){
            return (
                <Typography variant='body2'  component="div" style={{textAlign:"center"}} >
                    <IconGenerator type="cancel_appointment" />
                </Typography>
            )
        }
        else if(status === RequestStatus.CANCELED_BY_USER){
            return (
                <Typography variant='body2'  component="div" style={{textAlign:"center"}} >
                    <IconGenerator type="cancel_appointment_by_user" /> 
                </Typography>
            )
        }
        else{
            return (
                <Typography variant='body2'  component="div" style={{textAlign:"center"}} >
                    <RemoveCircleOutline style={{fontSize:"1.2rem", color:"red"}} />
                </Typography>
            )
        }
    }

    function resetModal(){
        setShowModal(false);
        setShowupAppointmentId(0);
        setCancelAppointmentId(0);
    }

    function showModalShowUp(idAppointment:number){
        setShowupAppointmentId(idAppointment);
        setShowModal(true);
    }

    function showModalCancel(idAppointment:number){
        setCancelAppointmentId(idAppointment);
        setShowModal(true);
    }

    function confirmShowUpPatient(){
        resetModal();
        showUpPatientCallback(showupAppointmentId);
    }

    function confirmCancelAppointment(byUser:boolean){
        resetModal();
        cancelAppointmentCallback(cancelAppointmentId, byUser);
    }

    function renderModal(){
        return (
            <Modal open={showModal} onClose={resetModal} 
                title={<Translate id="pages.hospital.outpatients.table_patient_appointments.modal.title" />} >
                <>
                {
                    showupAppointmentId !== 0 &&
                    <Grid container>
                        <Grid item xs={12} style={{paddingTop:'1rem'}}>
                            <Typography variant="body1" component="div" gutterBottom>
                                <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.show_up.message" />
                            </Typography>
                        </Grid>
                        <Grid item xs={12} style={{paddingTop:'1rem'}}>
                            <ButtonCancel onClick={ resetModal } data-testid="cancel-modal"  spaceright={1}>
                                <Translate id="general.cancel" />
                            </ButtonCancel>
                            &nbsp;
                            <ButtonContinue onClick={ confirmShowUpPatient } data-testid="continue-modal" >
                                <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.show_up.confirm" />
                            </ButtonContinue>
                        </Grid>
                    </Grid>
                }
                {
                    cancelAppointmentId !== 0 &&
                    <Grid container>
                        <Grid item xs={12} style={{paddingTop:'1rem'}}>
                            <Typography variant="body1" component="div" gutterBottom>
                                <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.cancel.question" />
                            </Typography>
                        </Grid>
                        <Grid item xs={12} style={{paddingTop:'1rem', paddingBottom:'0.5rem'}}>
                            <Button 
                                onClick={() => confirmCancelAppointment(false)} 
                                data-testid="cancel-regular"
                                fullWidth
                                style={{justifyContent: 'flex-start', textTransform: 'none', padding: '0.75rem'}}
                            >
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <IconGenerator type="cancel_appointment" />
                                    <Typography variant="body1" component="span" style={{marginLeft: '0.5rem'}}>
                                        <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.cancel.regular" />
                                    </Typography>
                                </div>
                            </Button>
                        </Grid>
                        <Grid item xs={12} style={{paddingBottom:'0.5rem'}}>
                            <Button 
                                onClick={() => confirmCancelAppointment(true)} 
                                data-testid="cancel-by-user"
                                fullWidth
                                style={{justifyContent: 'flex-start', textTransform: 'none', padding: '0.75rem'}}
                            >
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <IconGenerator type="cancel_appointment_by_user" />
                                    <Typography variant="body1" component="span" style={{marginLeft: '0.5rem'}}>
                                        <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.cancel.by_user" />
                                    </Typography>
                                </div>
                            </Button>
                        </Grid>
                        <Grid item xs={12} style={{paddingTop:'0.5rem'}}>
                            <ButtonCancel onClick={resetModal} data-testid="cancel-modal" fullWidth>
                                <Translate id="general.cancel" />
                            </ButtonCancel>
                        </Grid>
                        
                    </Grid>
                }
                </>
            </Modal>
        )
    }

    function renderAppointments(){
        if(loadingPatientsAppointments){
            return (
                <Grid item xs={12}>
                    <CircularProgress />
                </Grid>
            );
        }
        else{
            if(patientsAppointments.length === 0){
                return (
                    <Grid item xs={12}>
                        <p><Translate id="pages.hospital.outpatients.no_appointments_patient" /></p>
                    </Grid>
                );
            }
            else{
                const headCells = [ 
                                    { id: "department", alignment: "right", label: <Translate id="pages.hospital.outpatients.table_patient_appointments.department" /> },
                                    { id: "doctor", alignment: "right", label: <Translate id={`pages.hospital.outpatients.table_patient_appointments.doctor`} /> },
                                    { id: "date", alignment: "right", label: <Translate id={`pages.hospital.outpatients.table_patient_appointments.date`} /> },
                                    
                                    { id: "bookingDate", alignment: "right", label: <Translate id={`pages.hospital.outpatients.table_patient_appointments.bookingDate`} /> }, 
                                    { id: "status", alignment: "right", label: <Translate id={`pages.hospital.outpatients.table_patient_appointments.status`} /> }  
                                ];
                if(outpatientsInfo.consultation.type === "date_time"){
                    headCells.splice(2, 0, { id: "time", alignment: "right", label: <Translate id={`pages.hospital.outpatients.table_patient_appointments.time`} /> });
                }
                const rows = patientsAppointments.map((appointment:IAppointment) => {
                    return {
                        id : appointment.id,
                        uuid: appointment.uuid,
                        department: getAppointmentDepartmentName(appointment),
                        doctor : appointment.agenda?.principalResearcher?.researcher
                            ? researcherFullName(appointment.agenda.principalResearcher.researcher)
                            : "",
                        date : fullDateFromPostgresString(activeLanguage.code, appointment.startDateTime, outpatientsInfo.timezone || DEFAULT_CLINIC_TIMEZONE),
                        time : timeFromPostgresString( "es", appointment.startDateTime, outpatientsInfo.timezone || DEFAULT_CLINIC_TIMEZONE),
                        bookingDate : dateAndTimeFromPostgresString(activeLanguage.code, appointment.createdAt),
                        status: renderShowIcon(appointment.requestAppointment.status, appointment.startDateTime),
                        appointmentStatus : appointment.requestAppointment.status,
                        startDateTime : appointment.startDateTime,
                        billUuid: appointment.billUuid,
                        requestServiceInvestigationId: appointment.requestServiceInvestigationId,
                    }
                }).sort((a, b) => new Date(a.startDateTime) < new Date(b.startDateTime) ? 1 : -1);
                return(
                    <>
                    <EnhancedTable noHeader noSelectable={true} rows={rows} headCells={headCells}
                            actions={[{"type" : "view",
                                "tooltip": <Translate id="pages.hospital.outpatients.appointment.actions.view" />,
                                "check" : (row:any) => {
                                return true;
                            }, "func" : (id:number) => {
                                const appointment = patientsAppointments.find(a => a.id === id);
                                if(appointment) {
                                    const nextUrl = HOSPITAL_OUTPATIENTS_APPOINTMENT.replace(":uuidAppointment", appointment.uuid);
                                    history.push(nextUrl);
                                }
                            }},
                            {"type" : "cancel_appointment",
                                "tooltip": <Translate id="pages.hospital.outpatients.appointment.actions.cancel" />,
                                "check" : (row:any) => {
                                return canCancelAppointment(new Date(row.startDateTime), row.appointmentStatus, outpatientsInfo.consultation)
                            }, "func" : (id:number) => showModalCancel(id)},
                            {"type" : "billing",
                                "tooltip": <Translate id="pages.hospital.outpatients.appointment.actions.billing" />,
                                "check" : (row:any) => {
                                return row.appointmentStatus === RequestStatus.PENDING_PAYMENT
                            }, "func" : (id:number) => {
                                const billingUrl = generateBillingUrl(uuidPatient);
                                history.push(billingUrl);
                            }},
                            {"type" : "show_up",
                                "tooltip": <Translate id="pages.hospital.outpatients.appointment.actions.checkin" />,
                                "check" : (row:any) => {
                                return canShowUpAppointment(new Date(row.startDateTime), row.appointmentStatus, outpatientsInfo.consultation)
                            }, "func" : (id:number) => showModalShowUp(id)}]}
                            renderExtraActions={(row: any) =>
                                row.appointmentStatus === RequestStatus.PENDING_PAYMENT && row.billUuid ? (
                                    <QuickPayButton
                                        uuidInvestigation={uuidInvestigation}
                                        billUuid={row.billUuid}
                                        requestServiceInvestigationId={row.requestServiceInvestigationId}
                                        currency={currency}
                                        languageCode={activeLanguage.code}
                                        onSuccess={() => setLastUpdate(new Date())}
                                        size="medium"
                                    />
                                ) : null
                            }
                        />
                    </>
                )
            }
        }
        
    }
    if(!createAppointment){
        return (
            <Grid xs={12}>
               {
                renderModal()
               }
                <Grid item xs={12}>
                    <PatientInfo uuidPatient={uuidPatient} />
                </Grid>
                <Grid item xs={12}>
                    <ButtonAdd onClick={handleCreateAppointment} />
                    {
                        renderAppointments()
                    }
                </Grid>
            </Grid>
        );
    }
    return (
        <>
            <FormMakeAppointment showAllAgendas={false} uuidPatient={uuidPatient} uuidInvestigation={uuidInvestigation} 
                appointmentMadeCallback={appointmentMadeCallback} dateTimeAppointment={outpatientsInfo.consultation.type === "date_time"} 
                extraForm={outpatientsInfo.consultation.extraForm} typeAgenda={AgendasTypes.CONSULTATION}
                prefillTemplate={prefillTemplate}
                phoneNumber = {phoneNumber} cancelCallback={() => setCreateAppointment(false)}/> 
        </>
    );
};

export const PatientAppointmentInfoLocalized = withLocalize(PatientAppointmentInfoCore);